import useStoreApp from '@/store';
import { TourAdventureTime } from '@/types/consistency';
import { IPickUp, IPromotion } from '@/types/interface';
import { Tour, TourDetail, TourType } from '@/types/tourBooking';
import {
  convertMinutesToDaysMinutes,
  formatMillisecondsToHours,
  convertMillisecondsToTimeOfDay,
  toTimeId,
  millisecondsToTimeId,
} from '@/utils/formatTime';
import { handleLocalStorage } from '@/utils/handleLocalStorage';
import { hoiangoShowLog } from '@/utils/log';
import {
  contactNumberTypeOptionsList,
  formatDateDisplay,
  formatTimeDisplay,
} from '@/utils/utils';
import { trackTourBookingComplete, trackTourBookingStep } from '@/utils/gtag';
import moment, { Moment } from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

export type TimeItem = {
  id: number;
  hour: number;
  minute: number;
  format: string;
};

function validateReserveTime(
  futureDays: number,
  futureMinutes: number,
  time: Moment,
  date: Moment,
  tourType: TourType,
): boolean {
  const bookingTime = moment(time, formatTimeDisplay);
  const bookingDateTime = moment(date, formatDateDisplay).set({
    hour: bookingTime.get('hour'),
    minute: bookingTime.get('minute'),
  });

  // const currentTime = moment();
  const futureTime = moment()
    .add(futureDays, 'days')
    .add(futureMinutes, 'minutes');

  // const isBookingFromFutureDay = bookingDateTime.isAfter(futureTime);

  // // TODO: tour type: experience
  // if (tourType && tourType === TourType.EXPERIENCE) {
  //   return isBookingFromFutureDay;
  // }

  // // TODO: tour type: adventures -> Book from future day
  // else if (isBookingFromFutureDay) {
  //   return true;
  // }

  // // TODO: tour type: adventures -> Book in same day

  // const currentTimeOnly = moment(moment(), formatTime);

  return bookingDateTime.isAfter(futureTime);
}

interface IUseTourBookingProps {
  service: any;
  tourType?: TourType;
  currentTour: Tour | null;
}

export default function useTourBooking({
  service,
  tourType,
  currentTour,
}: IUseTourBookingProps) {
  const { t } = useTranslation(['validate', 'error', 'common']);

  const { isEmbeed, user } = useStoreApp(state => state.globalSlice);

  const isGoNow = {
    label: TourAdventureTime.goNow.title,
    value: 0,
  };

  const [pickUpData, setPickUpData] = useState<IPickUp | null>(null);
  const [isChangedTimer, setIsChangedTimer] = useState<boolean>(false);
  const [date, setDate] = useState<Moment>(moment());
  const [dateErrMsg, setDateErrMsg] = useState<string>('');
  const [time, setTime] = useState<Moment>(
    moment().add(
      user && currentTour?.reserveTime !== undefined
        ? currentTour.reserveTime
        : 30,
      'minutes',
    ),
  );
  const [timeErrMsg, setTimeErrMsg] = useState<string>('');
  const [successBooking, setSuccessBooking] = useState<boolean>(false);
  const [bookingErrMsg, setBookingErrMsg] = useState('');
  const [bookingDone, setBookingDone] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isPaypalProcessing, setIsPaypalProcessing] = useState<boolean>(false);

  const [minDate, setMinDate] = useState<Moment>(moment());

  const isAdventureTour = tourType === TourType.ADVENTURES;

  const [timeIdSelected, setTimeIdSelected] = useState<number>();

  const [promotion, setPromotion] = useState<null | IPromotion>(null);
  const [errorPromotion, setErrorPromotion] = useState<string>('');

  const [showPaypalPaymentModal, setShowPaypalPaymentModal] =
    useState<boolean>(false);
  const [paypalOrderId, setPaypalOrderId] = useState<string | undefined>(
    undefined,
  );
  const [paypalPaymentApproved, setPaypalPaymentApproved] =
    useState<boolean>(false);

  const [embedWaitingPayment, setEmbedWaitingPayment] =
    useState<boolean>(false);

  const contactNumberTypeOptions = contactNumberTypeOptionsList.map(
    (item: any) => ({
      label: item.label.includes('common:') ? t(item.label) : item.label,
      value: item.value,
    }),
  );

  const isCurrentDate = useMemo(() => {
    if (!isChangedTimer) return false;

    if (date) {
      return (
        date.format(formatDateDisplay) === moment().format(formatDateDisplay)
      );
    } else return false;
  }, [date]);

  useEffect(() => {
    if (!tourType) {
      return;
    }

    if (tourType === TourType.ADVENTURES) {
      initDateByAdventure();
    } else {
      initDateByExperience();
    }
  }, [tourType, currentTour]);

  useEffect(() => {
    const updateTimeIdSelected = () => {
      const timeId = toTimeId(time.hour(), time.minute());
      if (timeIdSelected !== timeId) {
        setTimeIdSelected(timeId);
      }
    };
    updateTimeIdSelected();
  }, [minDate]);

  function initDateByExperience() {
    let currentDate = moment();
    currentDate = currentDate.add(2, 'days');

    setDate(currentDate);
    setTime(currentDate);
    setMinDate(currentDate);
  }

  function initDateByAdventure() {
    if (!currentTour) {
      return;
    }

    // Always start fresh with today's date
    let currentDate = moment();

    // Check startActiveTime/endActiveTime logic
    // if user -> Go Now logic (current time)
    // else -> current + reserveTime
    if (user) {
      // Just moment() which is already set
    } else {
      currentDate.add(currentTour.reserveTime ?? 30, 'minute');
    }

    // Check if startActiveTime and endActiveTime are defined (0 is a valid value)
    if (
      currentTour.startActiveTime !== undefined &&
      currentTour.startActiveTime !== null &&
      currentTour.endActiveTime !== undefined &&
      currentTour.endActiveTime !== null
    ) {
      const nowMs =
        currentDate.hours() * 3600 * 1000 + currentDate.minutes() * 60 * 1000;
      const startActive = currentTour.startActiveTime;
      const endActive = currentTour.endActiveTime;

      if (nowMs < startActive) {
        // Current time is before tour start time, set to startActiveTime
        const totalMinutes = Math.floor(startActive / 60000);
        const hour = Math.floor(totalMinutes / 60);
        const minute = totalMinutes % 60;
        currentDate.set({ hour, minute, second: 0, millisecond: 0 });
      } else if (nowMs > endActive) {
        // Current time is after tour end time
        // Switch to tomorrow and set time to startActiveTime
        currentDate = moment().add(1, 'days');
        const totalMinutes = Math.floor(startActive / 60000);
        const hour = Math.floor(totalMinutes / 60);
        const minute = totalMinutes % 60;
        currentDate.set({ hour, minute, second: 0, millisecond: 0 });
      }
      // else: Current time is within range, use the calculated currentDate
    }

    setDate(currentDate);
    setTime(currentDate);
    setMinDate(currentDate);
  }

  // set error message for time, date inputs
  const setDateTimeErrMsg = useCallback(
    (validationError: string | null, type: 'date' | 'time') => {
      let text = '';
      if (validationError === 'invalidDate') {
        text = `${type}Invalid`;
      } else if (validationError === 'disablePast') {
        text = `${type}IsOver`;
      }

      let message = text !== '' ? t(`validate:${text}`) : '';

      type === 'date' ? setDateErrMsg(text) : setTimeErrMsg(message);
    },
    [],
  );

  const doUpdatePaymentType = useCallback(
    async (orderId: string, paymentType: string) => {
      setLoading(true);
      setSuccessBooking(false);
      try {
        const { data } = await service.updatePaymentType(orderId, {
          paymentType,
        });

        if (data) {
          setBookingDone(data);
        }

        setSuccessBooking(true);
        toast.success(t('common:updatePaymentTypeSuccess'));
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const doSubmit = useCallback(async (body: any) => {
    setLoading(true);
    setBookingErrMsg('');
    setSuccessBooking(false);

    try {
      const { data } = await service.createBooking(body);
      if (data) {
        setBookingDone(data);
      }

      setSuccessBooking(true);

      // Track successful booking completion
      if (data?.id) {
        trackTourBookingComplete(
          data.id,
          body.tourId || 'unknown',
          body.price || 0,
          'VND',
        );
      }
    } catch (error: any) {
      if (error?.response?.data?.message) {
        setBookingErrMsg(error?.response?.data?.message);
        toast.error(error?.response?.data?.message);
      } else {
        const msg = t('common:failBooking');
        setBookingErrMsg(`${msg}`);

        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const doSubmitByPartner = useCallback(async (body: any) => {
    setLoading(true);
    setBookingErrMsg('');
    setSuccessBooking(false);

    try {
      const { data } = await service.createBookingByPartner(body);

      if (data) {
        setBookingDone(data);
      }

      setSuccessBooking(true);

      // Track successful booking completion
      if (data?.id) {
        trackTourBookingComplete(
          data.id,
          body.tourId || 'unknown',
          body.price || 0,
          'VND',
        );
      }
    } catch (error: any) {
      if (error?.response?.data?.message) {
        setBookingErrMsg(error?.response?.data?.message);
        toast.error(error?.response?.data?.message);
      } else {
        const msg = t('common:failBooking');
        setBookingErrMsg(`${msg}`);

        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // check pickUp
  const checkPickUpAndDropOff = useCallback(
    (setValue: any, clearErrors: any) => {
      // if (!pickUpData) {
      //   setDistanceInformation({ km: 0, price: 0 });
      // }

      if (!!pickUpData) {
        clearErrors(['pickUp']);
      }
    },
    [pickUpData],
  );

  function validateBookingTime() {
    if (!currentTour || !currentTour.reserveTime || !tourType) {
      return;
    }

    const { days, minutes } = convertMinutesToDaysMinutes(
      currentTour.reserveTime,
    );

    if (
      timeIdSelected &&
      validateReserveTime(days, minutes, time, date, tourType)
    ) {
      setTimeErrMsg('');
      return;
    }

    var durationValue = '';
    var timeValue = '';

    if (days > 0) {
      durationValue = `${days}`;
      if (days > 1) {
        timeValue = ` ${t('common:days')} `;
      } else {
        timeValue = ` ${t('common:day')} `;
      }
    }

    if (minutes > 0) {
      if (durationValue !== '') {
        durationValue += `:${minutes}`;
      } else {
        durationValue = `${minutes}`;
      }

      if (timeValue === '') {
        if (minutes === 1) {
          timeValue = ` ${t('common:min')} `;
        } else {
          timeValue = ` ${t('common:mins')} `;
        }
      }
    }

    if (durationValue && timeValue) {
      const msg = t('validate:tourDate', {
        durationValue: durationValue,
        timeValue: timeValue,
      });

      setTimeErrMsg(msg);

      return;
    }
  }

  const validateTourWorkingTime = useCallback(
    (tourDetail?: Tour | null, timeIdSelectedParam?: number) => {
      const tourData = currentTour ?? tourDetail;

      let bookingMoment = null;

      let bookingTime = 0;
      if (timeIdSelectedParam !== undefined && timeIdSelectedParam !== null) {
        const hour = Math.floor(timeIdSelectedParam / 100);
        const minute = timeIdSelectedParam % 100;

        bookingTime = hour * 3600000 + minute * 60000;
      } else {
        bookingMoment = moment(time);
        bookingTime =
          bookingMoment.hours() * 3600000 + bookingMoment.minutes() * 60000;
      }

      if (tourData && tourData.startActiveTime && tourData.endActiveTime) {
        const startTimeNumber = tourData.startActiveTime || 0;
        const endTimeNumber = tourData.endActiveTime || 0;

        const tourStartTime = convertMillisecondsToTimeOfDay(startTimeNumber);
        const tourEndTime = convertMillisecondsToTimeOfDay(endTimeNumber);

        const isTimeInRange =
          bookingTime >= startTimeNumber && bookingTime <= endTimeNumber;

        if (!isTimeInRange) {
          const msg = t('validate:tourTime', {
            fromTime: tourStartTime,
            toTime: tourEndTime,
          });

          return msg;
        }
      }
      return null;
    },
    [currentTour, time],
  );

  const minTime = useMemo(() => {
    if (currentTour && date) {
      if (moment(date).isSame(moment(), 'day')) {
        let baseTime;
        if (user) {
          baseTime = moment().add(1, 'minutes');
        } else {
          baseTime = moment().add(currentTour.reserveTime || 0, 'minutes');
        }

        if (currentTour.startActiveTime) {
          const startTime = moment(date)
            .startOf('day')
            .add(currentTour.startActiveTime, 'milliseconds');
          if (baseTime.isBefore(startTime)) {
            return startTime;
          }
        }
        return baseTime;
      } else if (currentTour.startActiveTime) {
        return moment(date)
          .startOf('day')
          .add(currentTour.startActiveTime, 'milliseconds');
      }
    }
    return undefined;
  }, [currentTour, date, user]);

  const maxTime = useMemo(() => {
    if (currentTour && currentTour.endActiveTime && date) {
      return moment(date)
        .startOf('day')
        .add(currentTour.endActiveTime, 'milliseconds');
    }
    return undefined;
  }, [currentTour, date]);

  useEffect(() => {
    if (
      isAdventureTour &&
      minTime &&
      maxTime &&
      moment(date).isSame(moment(), 'day')
    ) {
      if (minTime.isAfter(maxTime)) {
        const startTimeNumber = currentTour?.startActiveTime || 0;
        const endTimeNumber = currentTour?.endActiveTime || 0;

        let tourStartTime = '';
        let tourEndTime = '';

        const formatTime = (val: number) => {
          const ms = val < 100000 ? val * 60000 : val;
          return convertMillisecondsToTimeOfDay(ms);
        };

        tourStartTime = formatTime(startTimeNumber);
        tourEndTime = formatTime(endTimeNumber);

        const msg = t('validate:tourTime', {
          fromTime: tourStartTime,
          toTime: tourEndTime,
        });
        setTimeErrMsg(msg);
      }
    }
  }, [minTime, maxTime, currentTour, isAdventureTour, date, t]);

  const handlePromotion = useCallback(
    async (value: string, collaboratorId?: string | null) => {
      try {
        const {
          data,
        }: {
          data: IPromotion;
        } = await service.getPromotionDetail(value.trim(), collaboratorId);

        if (!!data) {
          setErrorPromotion('');
          let discount = 0;

          setPromotion({
            ...data,
            discount: discount,
          });
        }
      } catch (error: any) {
        setPromotion(null);
        if (
          error?.response?.data?.message.includes(
            'Mã không tồn tại hoặc đã hết hạn sử dụng',
          )
        ) {
          const msg = t('error:error.promotion.1');
          setErrorPromotion(msg);
        } else setErrorPromotion(error?.response?.data?.message);
      }
    },
    [],
  );

  const createPaypalPayment = useCallback(async () => {
    if (bookingDone && bookingDone.id) {
      setIsPaypalProcessing(true);

      try {
        const { data } = await service.createPaypalPayment(bookingDone.id);
        return data.id;
      } catch (error: any) {
        if (error?.response?.status === 500) {
          toast.error(t('error:error.order.7') || '');
        }

        return null;
      } finally {
        setIsPaypalProcessing(false);
      }
    }
  }, [bookingDone, service, t]);

  const onCapturePaypalPayment = useCallback(async () => {
    if (bookingDone && bookingDone.id) {
      setIsPaypalProcessing(true);

      try {
        const res = await service.capturePaypalPayment(bookingDone.id);

        return res.data;
      } catch (error: any) {
        if (error?.response?.status === 500) {
          //setBookingErrMsg(t('error:error.order.7') || '');
          toast.error(t('error:error.order.7') || '');
        }
        return null;
      } finally {
        setIsPaypalProcessing(false);
      }
    }
  }, [bookingDone, service, t]);

  return {
    pickUpData,
    setPickUpData,
    isChangedTimer,
    setIsChangedTimer,
    date,
    setDate,
    dateErrMsg,
    time,
    setTime,
    timeErrMsg,
    successBooking,
    setSuccessBooking,
    bookingErrMsg,
    setBookingErrMsg,
    bookingDone,
    loading,
    isCurrentDate,
    setDateTimeErrMsg,
    doSubmit,
    doSubmitByPartner,
    checkPickUpAndDropOff,
    minDate,
    timeIdSelected,
    setTimeIdSelected,
    contactNumberTypeOptions,
    // isValidScheduleDate,
    handlePromotion,
    promotion,
    setPromotion,
    errorPromotion,
    setErrorPromotion,
    // reComputePrice
    // getDiscountPrice
    validateBookingTime,
    validateTourWorkingTime,
    isPaypalProcessing,
    createPaypalPayment,
    onCapturePaypalPayment,
    isGoNow,
    showPaypalPaymentModal,
    setShowPaypalPaymentModal,
    paypalOrderId,
    setPaypalOrderId,
    paypalPaymentApproved,
    setPaypalPaymentApproved,
    embedWaitingPayment,
    setEmbedWaitingPayment,
    minTime,
    maxTime,
    doUpdatePaymentType,
  };
}
